/**
 * ErrorBoundary — catches unhandled JS exceptions and shows recovery screen.
 * Uses key-based remount for full subtree reset.
 */

import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Linking } from 'react-native';
import { LIGHT_COLORS } from '../../utils/theme';
import { APP_VERSION } from '../../utils/constants';
import { FONT_SIZE, SPACING, RADIUS } from '../../utils/design-tokens';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  resetKey: number;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null, resetKey: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private handleReset = () => {
    this.setState((prev) => ({ hasError: false, error: null, resetKey: prev.resetKey + 1 }));
  };

  private handleReport = () => {
    const subject = encodeURIComponent('Bug Report — FishMemory');
    const body = encodeURIComponent(
      `\n\n--- Debug Info ---\nVersion: ${APP_VERSION}\nError: ${this.state.error?.message || 'Unknown'}\n`
    );
    Linking.openURL(`mailto:support@fishmemory.net?subject=${subject}&body=${body}`);
  };

  render() {
    if (this.state.hasError) {
      return (
        <View style={styles.container}>
          <Text style={styles.emoji}>😵</Text>
          <Text style={styles.title}>Something went wrong</Text>
          <Text style={styles.message}>
            An unexpected error occurred. Please try again.
          </Text>
          <Text style={styles.version}>v{APP_VERSION}</Text>
          <TouchableOpacity style={styles.button} onPress={this.handleReset}>
            <Text style={styles.buttonText}>Try Again</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.linkButton} onPress={this.handleReport}>
            <Text style={styles.linkText}>Report Issue</Text>
          </TouchableOpacity>
        </View>
      );
    }
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: LIGHT_COLORS.background,
    padding: SPACING['2xl'],
  },
  emoji: { fontSize: 64, marginBottom: SPACING.base },
  title: { fontSize: FONT_SIZE.lg, fontWeight: '700', color: LIGHT_COLORS.text, marginBottom: SPACING.sm },
  message: { fontSize: FONT_SIZE.sm, color: LIGHT_COLORS.textSecondary, textAlign: 'center', marginBottom: SPACING.sm },
  version: { fontSize: FONT_SIZE.xs, color: LIGHT_COLORS.textSecondary, marginBottom: SPACING.xl },
  button: {
    backgroundColor: LIGHT_COLORS.primary,
    borderRadius: RADIUS.sm,
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.xl,
  },
  buttonText: { color: '#fff', fontWeight: '600', fontSize: FONT_SIZE.base },
  linkButton: { marginTop: SPACING.base, padding: SPACING.sm },
  linkText: { color: LIGHT_COLORS.primary, fontSize: FONT_SIZE.sm, fontWeight: '500' },
});
